import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { clientAPI } from '../services/ApiService';

const ClientManagementPage = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    contactNumber: '',
    location: '',
    plantationType: '',
    soilProfile: '',
    plantationAge: '',
    fertilizerType: '',
    notes: ''
  });
  
  const plantationTypes = [
    'Palm Oil',
    'Fruit Orchard', 
    'Rubber',
    'Coconut',
    'Cocoa',
    'Mixed Crops',
    'Durian',
    'Corn',
    'Rice',
    'Other'
  ];
  
  const soilProfiles = [
    'Sandy',
    'Clay',
    'Loamy',
    'Silty',
    'Peaty',
    'Chalky',
    'Laterite',
    'Mixed'
  ];
  
  useEffect(() => {
    loadClients();
  }, []);
  
  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const clientsData = await clientAPI.getAllClients();
      setClients(clientsData);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load client data: ' + err.message);
      setSnackbar({
        open: true,
        message: 'Failed to load client data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      companyName: '',
      email: '',
      contactNumber: '',
      location: '',
      plantationType: '',
      soilProfile: '',
      plantationAge: '',
      fertilizerType: '',
      notes: ''
    });
    setEditingClient(null);
  };
  
  const openNewClientDialog = () => {
    resetForm();
    setOpenDialog(true);
  };
  
  const openEditClientDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      companyName: client.companyName || '',
      email: client.email || '',
      contactNumber: client.contactNumber || '',
      location: client.location || '',
      plantationType: client.plantationType,
      soilProfile: client.soilProfile || '',
      plantationAge: client.plantationAge || '',
      fertilizerType: client.fertilizerType || '',
      notes: client.notes || ''
    });
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };
  
  const validateForm = () => {
    if (!formData.name || !formData.plantationType) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields (Name and Plantation Type)',
        severity: 'error'
      });
      return false;
    }
    return true;
  };
  
  const handleSaveClient = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingClient) {
        // Update existing client
        await clientAPI.updateClient(editingClient.id, formData);
        setSnackbar({
          open: true,
          message: 'Client updated successfully',
          severity: 'success'
        });
      } else {
        // Create new client
        await clientAPI.createClient(formData);
        setSnackbar({
          open: true,
          message: 'Client created successfully',
          severity: 'success'
        });
      }
      
      // Reload clients and close dialog
      await loadClients();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving client:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save client: ' + err.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteClient = async (clientId, clientName) => {
    if (window.confirm(`Are you sure you want to delete client "${clientName}"?`)) {
      setLoading(true);
      try {
        await clientAPI.deleteClient(clientId);
        setSnackbar({
          open: true,
          message: 'Client deleted successfully',
          severity: 'success'
        });
        await loadClients();
      } catch (err) {
        console.error('Error deleting client:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete client: ' + err.message,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  // Filter clients based on search and filter
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.companyName && client.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (client.location && client.location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterType === 'all' || client.plantationType === filterType;
    
    return matchesSearch && matchesFilter;
  });

  if (loading && clients.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading clients...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client Management
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Manage your agricultural clients and their plantation details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={<FilterListIcon />}
              >
                <MenuItem value="all">All Types</MenuItem>
                {plantationTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openNewClientDialog}
              disabled={loading}
            >
              Add New Client
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Client Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {clients.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="secondary">
                {clients.filter(c => c.plantationType === 'Palm Oil').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Palm Oil Farms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {clients.filter(c => c.isActive).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {new Set(clients.map(c => c.plantationType)).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plantation Types
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clients Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Plantation Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || filterType !== 'all' 
                        ? 'No clients match your search criteria' 
                        : 'No clients found. Add your first client to get started!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="subtitle2">
                            {client.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{client.companyName || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={client.plantationType} 
                        color="primary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{client.location || '-'}</TableCell>
                    <TableCell>{client.contactNumber || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit Client">
                        <IconButton
                          size="small"
                          onClick={() => openEditClientDialog(client)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Client">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClient(client.id, client.name)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Client Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingClient ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Client Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl required fullWidth margin="normal">
                <InputLabel>Plantation Type</InputLabel>
                <Select
                  name="plantationType"
                  value={formData.plantationType}
                  onChange={handleInputChange}
                  label="Plantation Type"
                >
                  {plantationTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Soil Profile</InputLabel>
                <Select
                  name="soilProfile"
                  value={formData.soilProfile}
                  onChange={handleInputChange}
                  label="Soil Profile"
                >
                  {soilProfiles.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Plantation Age (years)"
                name="plantationAge"
                type="number"
                value={formData.plantationAge}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Fertilizer Type"
                name="fertilizerType"
                value={formData.fertilizerType}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveClient}
            disabled={loading}
            startIcon={editingClient ? <EditIcon /> : <AddIcon />}
          >
            {loading ? 'Saving...' : (editingClient ? 'Update Client' : 'Add Client')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientManagementPage; 